<template>
  <div class="diagram-output">
    <div class="toolbar">
      <div class="toolbar-left">
        <span class="toolbar-hint">💡 提示：表头属性太多？点击"显示配置"选择需要的字段后导出，最后别忘记修改为自己学校的格式</span>
      </div>
      <div class="toolbar-right">
        <button class="tool-btn btn-red" @click="openStyleDialog">
          <i class="el-icon-setting"></i>显示配置
        </button>
        <button class="tool-btn btn-blue" @click="handleExport">
          <i class="el-icon-download"></i>导出Word
        </button>
      </div>
    </div>
    <div class="diagram-wrapper">
      <div v-if="error" class="error-message">{{ error }}</div>
      <!-- 空状态提示 -->
      <div v-else-if="!tables || tables.length === 0" class="empty-state">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="64" height="64">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
            d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
        </svg>
        <p class="empty-title">暂无表格数据</p>
        <p class="empty-description">请在左侧输入SQL建表语句，然后点击"生成三线表"按钮生成</p>
      </div>
      <div v-else class="docx-preview-container">
        <div v-if="loading" class="loading-wrapper">
          <el-skeleton :rows="10" animated />
        </div>
        <div v-else ref="previewContainer" class="word-preview-wrapper"></div>
      </div>
    </div>
  
    <!-- 显示配置对话框 -->
    <el-dialog
      :visible.sync="styleDialogVisible"
      title="表格显示配置"
      width="620px"
      :before-close="handleDialogClose"
      destroy-on-close
    >
      <div class="column-config">
        <div v-for="(config, key) in columnConfig" :key="key" class="config-item">
          <el-switch v-model="config.visible" @change="(val) => toggleColumn(key, val)" />
          <span class="config-label">{{ key }}</span>
        </div>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="resetToDefault">恢复默认</el-button>
          <el-button type="primary" @click="saveConfig">
            确定
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
 
</template>

<script>
import {
  AlignmentType,
  BorderStyle,
  Document,
  HeadingLevel,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  VerticalAlign,
  WidthType,
} from 'docx';

export default {
  name: 'TableInfo',
  
  data() {
    return {
      loading: false,
      error: '',
      tables: [],
      docxBlob: null,
      styleDialogVisible: false,
      previewContainer: null,
      customStyleElement: null,
      columnConfig: {
        序号: { visible: true },
        字段名: { visible: true },
        字段类型: { visible: true },
        长度: { visible: true },
        允许空: { visible: true },
        主键: { visible: true },
        默认值: { visible: true },
        说明: { visible: true },
      }
    };
  },

  mounted() {
    this.loadConfig();
    this.$nextTick(() => {
      if (this.tables && this.tables.length > 0) {
        this.safeRenderTables();
      }
    });
  },

  beforeDestroy() {
    if (this.customStyleElement && document.head.contains(this.customStyleElement)) {
      document.head.removeChild(this.customStyleElement);
    }

    if (this.docxBlob) {
      URL.revokeObjectURL(URL.createObjectURL(this.docxBlob));
      this.docxBlob = null;
    }
  },

  watch: {
    columnConfig: {
      handler() {
        this.docxBlob = null;
        if (this.tables && this.tables.length > 0) {
          this.safeRenderTables();
        }
      },
      deep: true
    },
    tables: {
      handler() {
        if (this.tables && this.tables.length > 0) {
          this.docxBlob = null;
          this.safeRenderTables();
        }
      },
      deep: true
    }
  },

  methods: {
    openStyleDialog() {
      this.styleDialogVisible = true;
    },

    handleDialogClose() {
      this.styleDialogVisible = false;
    },

    async updateTableInfo(newTables) {
      if (!newTables || !Array.isArray(newTables)) {
        this.error = '接收到无效的表格数据';
        return;
      }

      this.tables = newTables;

      if (newTables && newTables.length > 0) {
        await this.$nextTick();
        this.safeRenderTables();
      } else {
        this.error = '没有表格数据可显示';
      }
    },

    safeRenderTables() {
      if (!this.$refs.previewContainer) {
        this.$nextTick(() => {
          if (this.$refs.previewContainer) {
            this.renderFallbackTables();
          }
        });
        return;
      }

      this.renderFallbackTables();
    },

    renderFallbackTables() {
      if (!this.$refs.previewContainer) return;

      if (!this.tables || this.tables.length === 0) {
        this.$refs.previewContainer.innerHTML = '<div class="empty-state"><svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="64" height="64"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg><p class="empty-title">暂无表格数据</p><p class="empty-description">请在左侧输入SQL建表语句，然后点击"生成三线表"按钮生成</p></div>';
        return;
      }

      try {
        let loadingTimer = null;
        if (this.tables && this.tables.length > 10) {
          loadingTimer = setTimeout(() => {
            this.loading = true;
          }, 100);
        }

        const scrollPosition = this.$refs.previewContainer.scrollTop || 0;
        const htmlContent = this.createFallbackPreview();
        this.$refs.previewContainer.innerHTML = htmlContent;

        if (loadingTimer) {
          clearTimeout(loadingTimer);
        }
        this.loading = false;

        setTimeout(() => {
          if (this.$refs.previewContainer) {
            this.$refs.previewContainer.scrollTop = scrollPosition;
          }
        }, 50);
      } catch (err) {
        console.error('渲染表格时出错:', err);
        if (this.$refs.previewContainer) {
          this.$refs.previewContainer.innerHTML = `<div class="error-message">渲染表格时出错: ${err.message}</div>`;
        }
        this.error = '渲染表格时出错: ' + (err.message || '未知错误');
      }
    },

    toggleColumn(columnName, isVisible) {
      this.columnConfig[columnName].visible = isVisible;
      this.safeRenderTables();
    },

    saveConfig() {
      localStorage.setItem('tableColumnConfig', JSON.stringify(this.columnConfig));
      this.styleDialogVisible = false;
      this.docxBlob = null;
      this.safeRenderTables();
      this.$message.success('配置已应用');
    },

    resetToDefault() {
      this.columnConfig = {
        序号: { visible: true },
        字段名: { visible: true },
        字段类型: { visible: true },
        长度: { visible: true },
        允许空: { visible: true },
        主键: { visible: true },
        默认值: { visible: true },
        说明: { visible: true },
      };
      this.docxBlob = null;
      this.safeRenderTables();
      this.$message.success('已恢复默认配置');
    },

    loadConfig() {
      try {
        const savedConfig = localStorage.getItem('tableColumnConfig');
        if (savedConfig) {
          try {
            const parsedConfig = JSON.parse(savedConfig);
            Object.keys(this.columnConfig).forEach(key => {
              if (typeof parsedConfig[key] === 'undefined') {
                parsedConfig[key] = this.columnConfig[key];
              }
            });
            this.columnConfig = parsedConfig;
          } catch (e) {
            console.error('解析保存的配置失败:', e);
            this.columnConfig = JSON.parse(JSON.stringify(this.columnConfig));
          }
        }
      } catch (err) {
        console.error('加载配置时出错:', err);
        this.columnConfig = JSON.parse(JSON.stringify(this.columnConfig));
      }
    },

    isPrimaryKey(table, columnName) {
      return table.primaryKeys?.includes(columnName) || false;
    },

    createFallbackPreview() {
      if (!this.tables || !Array.isArray(this.tables) || this.tables.length === 0) {
        return '<div class="empty-state"><svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="64" height="64"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg><p class="empty-title">暂无表格数据</p><p class="empty-description">请在左侧输入SQL建表语句，然后点击"生成三线表"按钮生成</p></div>';
      }

      let html = '';

      this.tables.forEach((table, index) => {
        if (!table || !Array.isArray(table.columns)) {
          console.warn('无效表格数据:', table);
          return;
        }

        const tableNumber = index + 1;
        const tableName = table.tableName || `未命名表${tableNumber}`;

        html += `<h3 style="font-size:16px; font-weight:bold; margin:24px 0 16px;">表${tableNumber} ${this.escapeHtml(tableName)}</h3>`;
        html += '<table style="width:100%; border-collapse:collapse; margin-bottom:32px; font-size:14px; color:rgba(0,0,0,0.6);">';

        html += '<thead><tr style="border-top:2px solid #000; border-bottom:1px solid #000;">';
        if (this.columnConfig.序号.visible) html += '<th style="padding:10px 12px; text-align:center; vertical-align:middle; border:none;">序号</th>';
        if (this.columnConfig.字段名.visible) html += '<th style="padding:10px 12px; text-align:center; vertical-align:middle; border:none;">字段名</th>';
        if (this.columnConfig.字段类型.visible) html += '<th style="padding:10px 12px; text-align:center; vertical-align:middle; border:none;">字段类型</th>';
        if (this.columnConfig.长度.visible) html += '<th style="padding:10px 12px; text-align:center; vertical-align:middle; border:none;">长度</th>';
        if (this.columnConfig.主键.visible) html += '<th style="padding:10px 12px; text-align:center; vertical-align:middle; border:none;">主键</th>';
        if (this.columnConfig.允许空.visible) html += '<th style="padding:10px 12px; text-align:center; vertical-align:middle; border:none;">允许空</th>';
        if (this.columnConfig.默认值.visible) html += '<th style="padding:10px 12px; text-align:center; vertical-align:middle; border:none;">默认值</th>';
        if (this.columnConfig.说明.visible) html += '<th style="padding:10px 12px; text-align:center; vertical-align:middle; border:none;">说明</th>';
        html += '</tr></thead>';

        html += '<tbody>';
        table.columns.forEach((col, colIndex) => {
          const isLastRow = colIndex === table.columns.length - 1;
          const rowStyle = isLastRow ? 'style="border-bottom:2px solid #000;"' : '';

          html += `<tr ${rowStyle}>`;
          if (this.columnConfig.序号.visible) html += `<td style="padding:10px 12px; text-align:center; vertical-align:middle; border:none;">${colIndex + 1}</td>`;
          if (this.columnConfig.字段名.visible) html += `<td style="padding:10px 12px; text-align:center; vertical-align:middle; border:none;">${this.escapeHtml(col.name || '')}</td>`;
          if (this.columnConfig.字段类型.visible) html += `<td style="padding:10px 12px; text-align:center; vertical-align:middle; border:none;">${this.escapeHtml(col.type || '')}</td>`;
          if (this.columnConfig.长度.visible) html += `<td style="padding:10px 12px; text-align:center; vertical-align:middle; border:none;">${col.length || '-'}</td>`;
          if (this.columnConfig.主键.visible) html += `<td style="padding:10px 12px; text-align:center; vertical-align:middle; border:none;">${this.isPrimaryKey(table, col.name) ? '是' : '否'}</td>`;
          if (this.columnConfig.允许空.visible) html += `<td style="padding:10px 12px; text-align:center; vertical-align:middle; border:none;">${col.nullable ? '是' : '否'}</td>`;
          if (this.columnConfig.默认值.visible) {
            let defaultValue = col.defaultValue;
            if (defaultValue === null || defaultValue === undefined) {
              defaultValue = '-';
            } else {
              defaultValue = this.escapeHtml(String(defaultValue));
            }
            html += `<td style="padding:10px 12px; text-align:center; vertical-align:middle; border:none;">${defaultValue}</td>`;
          }
          if (this.columnConfig.说明.visible) {
            const comment = col.comment ? this.escapeHtml(col.comment) : '';
            html += `<td style="padding:10px 12px; text-align:center; vertical-align:middle; border:none;">${comment}</td>`;
          }
          html += '</tr>';
        });

        html += '</tbody></table>';
      });

      return `<div style="font-family:宋体,SimSun,sans-serif;">${html}</div>`;
    },

    escapeHtml(unsafe) {
      if (unsafe === null || unsafe === undefined) return '';
      return String(unsafe)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },

    async generateDocx() {
      if (!this.tables || this.tables.length === 0) {
        this.error = '没有表格数据可生成';
        return null;
      }

      try {
        if (this.docxBlob) {
          return this.docxBlob;
        }

        const children = this.tables.flatMap((table, index) => {
          if (!table || !Array.isArray(table.columns)) {
            console.error('Invalid table or columns:', table);
            return [];
          }

          const tableNumber = index + 1;

          const headerCells = [];
          if (this.columnConfig.序号.visible) headerCells.push(this.createHeaderCell("序号"));
          if (this.columnConfig.字段名.visible) headerCells.push(this.createHeaderCell("字段名"));
          if (this.columnConfig.字段类型.visible) headerCells.push(this.createHeaderCell("字段类型"));
          if (this.columnConfig.长度.visible) headerCells.push(this.createHeaderCell("长度"));
          if (this.columnConfig.主键.visible) headerCells.push(this.createHeaderCell("主键"));
          if (this.columnConfig.允许空.visible) headerCells.push(this.createHeaderCell("允许空"));
          if (this.columnConfig.默认值.visible) headerCells.push(this.createHeaderCell("默认值"));
          if (this.columnConfig.说明.visible) headerCells.push(this.createHeaderCell("说明"));

          const table3Line = new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
              top: { style: BorderStyle.SINGLE, size: 15, color: "000000" },
              bottom: { style: BorderStyle.SINGLE, size: 15, color: "000000" },
              left: { style: BorderStyle.NONE },
              right: { style: BorderStyle.NONE },
              insideHorizontal: { style: BorderStyle.NONE },
              insideVertical: { style: BorderStyle.NONE },
            },
            rows: [
              new TableRow({
                tableHeader: true,
                children: headerCells,
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 15, color: "000000" },
                  bottom: { style: BorderStyle.SINGLE, size: 10, color: "000000" },
                },
              }),
              ...table.columns.map((col, colIndex) => {
                const cells = [];

                if (this.columnConfig.序号.visible) cells.push(this.createCell((colIndex + 1).toString()));
                if (this.columnConfig.字段名.visible) cells.push(this.createCell(col.name || ''));
                if (this.columnConfig.字段类型.visible) cells.push(this.createCell(col.type || ''));
                if (this.columnConfig.长度.visible) cells.push(this.createCell(col.length || '-'));
                if (this.columnConfig.主键.visible) cells.push(this.createCell(this.isPrimaryKey(table, col.name) ? '是' : '否'));
                if (this.columnConfig.允许空.visible) cells.push(this.createCell(col.nullable ? '是' : '否'));
                if (this.columnConfig.默认值.visible) cells.push(this.createCell(col.defaultValue || '-'));
                if (this.columnConfig.说明.visible) cells.push(this.createCell(col.comment || ''));

                if (colIndex === table.columns.length - 1) {
                  return new TableRow({
                    children: cells,
                    borders: {
                      bottom: { style: BorderStyle.SINGLE, size: 15, color: "000000" },
                    },
                  });
                }

                return new TableRow({
                  children: cells,
                });
              }),
            ],
          });

          return [
            new Paragraph({
              text: `表${tableNumber} ${table.tableName}`,
              heading: HeadingLevel.HEADING_3,
              spacing: {
                before: 400,
                after: 200,
              },
              alignment: AlignmentType.LEFT,
            }),
            table3Line,
            new Paragraph({
              spacing: {
                before: 400,
                after: 400,
              },
            }),
          ];
        });

        const doc = new Document({
          sections: [{
            properties: {
              page: {
                margin: {
                  top: 1440,
                  right: 1440,
                  bottom: 1440,
                  left: 1440,
                },
              },
            },
            children: children,
          }],
          styles: {
            paragraphStyles: [
              {
                id: "Heading3",
                name: "Heading 3",
                basedOn: "Normal",
                next: "Normal",
                quickFormat: true,
                run: {
                  size: 28,
                  bold: true,
                  font: "宋体",
                },
                paragraph: {
                  spacing: {
                    before: 400,
                    after: 200,
                  },
                },
              },
              {
                id: "Normal",
                name: "Normal",
                basedOn: "Normal",
                next: "Normal",
                run: {
                  size: 24,
                  font: "宋体",
                },
                paragraph: {
                  spacing: {
                    line: 276,
                  },
                },
              },
            ],
          },
        });

        const blob = await Packer.toBlob(doc);
        this.docxBlob = blob;
        return blob;
      } catch (err) {
        console.error('生成DOCX文档出错:', err);
        this.error = '生成文档时出错';
        return null;
      }
    },

    createHeaderCell(text) {
      return new TableCell({
        borders: {
          bottom: { style: BorderStyle.SINGLE, size: 6, color: "000000" },
          top: { style: BorderStyle.NONE },
          left: { style: BorderStyle.NONE },
          right: { style: BorderStyle.NONE },
        },
        children: [new Paragraph({
          text: text,
          alignment: AlignmentType.CENTER,
        })],
        verticalAlign: VerticalAlign.CENTER,
      });
    },

    createCell(text) {
      return new TableCell({
        borders: {
          top: { style: BorderStyle.NONE },
          bottom: { style: BorderStyle.NONE },
          left: { style: BorderStyle.NONE },
          right: { style: BorderStyle.NONE },
        },
        children: [new Paragraph({
          text: text,
          alignment: AlignmentType.CENTER,
        })],
        verticalAlign: VerticalAlign.CENTER,
      });
    },

    async handleExport() {
      try {
        this.loading = true;
        this.error = '';

        if (!this.docxBlob) {
          this.docxBlob = await this.generateDocx();
        }

        if (this.docxBlob) {
          const url = URL.createObjectURL(this.docxBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = '数据库设计文档.docx';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          this.$message.success('文档导出成功');
        } else {
          this.$message.error('生成文档时出错');
        }
      } catch (err) {
        console.error('导出文档出错:', err);
        this.error = '导出文档时出错: ' + (err.message || '未知错误');
        this.$message.error('导出文档时出错');
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          if (this.tables && this.tables.length > 0) {
            this.safeRenderTables();
          }
        });
      }
    }
  }
};
</script>

<style scoped>
.diagram-output {
  background: #FFFFFF;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 24px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  min-height: 50px;
}

.toolbar-left { display: flex; gap: 10px; }
.toolbar-right { display: flex; gap: 10px; }

.toolbar-hint {
  font-size: 14px;
  color: #856404;
  background: #fff3cd;
  padding: 6px 14px;
  border-radius: 4px;
  display: flex;
  align-items: center;
}

.tool-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 14px;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}

.tool-btn i { font-size: 15px; }

.btn-red { background: #ff6b6b; }
.btn-red:hover { background: #e55c5c; }
.btn-blue { background: #409eff; }
.btn-blue:hover { background: #2b88e6; }

.diagram-wrapper {
  flex: 1;
  background-color: #FFFFFF;
  overflow-y: auto;
  
}

/* 空状态样式 */
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: #fafafa;
  height: 100%;
}

.empty-state svg {
  color: #d1d5db;
  margin-bottom: 20px;
}

.empty-title {
  font-size: 14px;
  font-weight: normal;
  margin: 0 0 8px 0;
}

.empty-description {
  font-size: 14px;
  font-weight: normal;
  margin: 0;
  text-align: center;
  line-height: 1.6;
}

.diagram-wrapper .error-message {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #F56C6C;
  font-size: 16px;
}

.docx-preview-container {
  margin: 0 auto;
  max-width: 100%;
  padding: 30px;
}

.docx-preview-container .loading-wrapper {
  padding: 20px;
}

.docx-preview-container .word-preview-wrapper {
  width: 100%;
  height: 100%;
}

.column-config {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  max-height: 400px;
  overflow-y: auto;
  padding: 8px;
}

.config-item {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding: 8px;
  border-radius: 4px;
  width: 110px;
  background-color: #f9f9f9;
  transition: background-color 0.3s;
}

.config-item:hover {
  background-color: #f0f0f0;
}

.config-label {
  margin-left: 12px;
  flex: 1;
  font-weight: 500;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 8px;
}
</style>
